import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { CardSetProvider, useCardSet } from "./contexts/CardSetContext.js";
import { useAuthStore } from "./store/useAuthStore.js";
import { useGameStore } from "./store/useGameStore.js";
import { StartScreen } from "./pages/StartScreen.js";
import { GamePage } from "./pages/GamePage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { LeaderboardPage } from "./pages/LeaderboardPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { LogoutPage } from "./pages/LogoutPage.js";
import { TABLE_BACKGROUNDS } from "./config.js";
import { loadPreferences } from "./lib/preferences.js";
import { LoadingScreen } from "./components/LoadingScreen.js";
import { I18nProvider } from "./i18n/index.js";

// ============================================================
// AppInner — innerhalb von CardSetProvider, damit useCardSet nutzbar ist
// ============================================================

function AppInner() {
  const initialize = useAuthStore((s) => s.initialize);
  const user       = useAuthStore((s) => s.user);
  const { tableBackground, setTableBackground, setSelectedSet, setLeftHanded, setAnimationSpeed } = useCardSet();

  // Auth-Session beim Start laden
  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Grafik-Präferenzen aus DB laden sobald User bekannt
  useEffect(() => {
    if (!user) return;
    loadPreferences(user.id).then((prefs) => {
      if (!prefs) return;
      setSelectedSet(prefs.cardSet, prefs.backName);
      setTableBackground(prefs.tableBackground);
      setLeftHanded(prefs.leftHanded);
      setAnimationSpeed(prefs.animationSpeed);
      useGameStore.getState().setAutoAce(prefs.autoAce);
    });
  // Nur wenn sich user.id ändert (Login/Logout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // CSS-Variable reaktiv halten (ändert sich wenn User Farbe wechselt)
  useEffect(() => {
    const color = TABLE_BACKGROUNDS[tableBackground]?.color ?? TABLE_BACKGROUNDS.casinoGreen.color;
    document.documentElement.style.setProperty("--table-bg", color);
  }, [tableBackground]);

  return (
    <Routes>
      <Route path="/"            element={<LoginPage defaultTab="login" />} />
      <Route path="/login"       element={<LoginPage defaultTab="login" />} />
      <Route path="/register"    element={<LoginPage defaultTab="register" />} />
      <Route path="/start"       element={<StartScreen />} />
      <Route path="/game"        element={<GamePage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/settings"    element={<SettingsPage />} />
      <Route path="/profile"     element={<ProfilePage />} />
      <Route path="/logout"      element={<LogoutPage />} />
    </Routes>
  );
}

// ============================================================
// App — CardSetProvider muss außen sein damit AppInner useCardSet nutzen kann
// ============================================================

export function App() {
  const [loading, setLoading] = useState(true);

  if (loading) {
    return <LoadingScreen onDone={() => setLoading(false)} />;
  }

  return (
    <I18nProvider>
      <CardSetProvider>
        <AppInner />
      </CardSetProvider>
    </I18nProvider>
  );
}
